import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-native-modal';
import {
  ActivityIndicator,
  View,
  Text,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  Image,
  Platform,
} from 'react-native';

import Colors from '../../constants/Colors';
import Layout from '../../constants/Layout';
import { capitalize } from '../../utils/stringFunctions';
import timeUtils from '../../utils/timeUtils';
import {
  getFieldValidationErrors,
  expandFields,
  typeFormatters,
  formatField,
  getCheckedList,
  getQuestionByUUID,
  getLabelAndValueForSpecialField,
  getUserUpdateOptionsAsString,
  formatters,
  convertToString,
  getSpecialTypeParentField,
  isBlankField,
} from '../../utils/additionalInfo';
import Selector from '../form/Selector';
import Svg from '../Svg';
import AddressSelector from '../profile/AddressSelector';
import MyFloatingLabelInput from '../form/MyFloatingLabelInput';
import DateSelectModal from '../form/BirthdaySelectModal';
import PlusMinus from '../form/PlusMinus';
import forceComponentUpdate from '../../utils/forceComponentUpdate';
import schema from '../../assets/schemas/additionalInfoQuestions.json';
let Sentry;
if (Platform.OS === 'web') {
  Sentry = require('@sentry/react');
} else {
  Sentry = require('@sentry/react-native');
}

function MissingInfoModal(props) {
  const {
    translation,
    profile,
    actions,
    pantry,
    locale,
    onBackdropPress,
    missingFields,
    requestedFields,
    setConfirmAdditionalInfo,
  } = props;

  const year2000 = 946750000;

  // if requested, show the following fields as disabled (so clients know they are submitted to the pantry)
  const alwaysInclude = ['birth_date', 'zip_code'];

  const [errors, setErrors] = useState({});
  const [requiredIndex, setRequiredIndex] = useState(-1);
  const [isConfirming, setIsConfirming] = useState(false);
  const [user, setUser] = useState(Object.assign({}, profile));
  const [isComplete, setIsComplete] = useState(false);
  const [showDateModal, setShowDateModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tyMsg, setTyMsg] = useState(false); // show the thank you message at the very end
  const [isConfirmed, setIsConfirmed] = useState(false); // are the fields on the confirm page confirmed?
  const [rerenderFlag, setRerenderFlag] = useState(false);
  const [isSubmittingData, setIsSubmittingData] = useState(false);
  const [checkedList, setCheckedList] = useState([...alwaysInclude]);

  const labels = {
    first_name: capitalize(translation.app_personal_edittext_firstname),
    last_name: capitalize(translation.app_personal_edittext_lastname),
    birth_date: capitalize(translation.dob_required),
    zip_code: translation.app_personal_edittext_zipcode,
    phone: translation.app_personal_edittext_phone,
    email: translation.email_address,
    emergency_contact: capitalize(translation.fragment_profile_textview_emergencycontact),
    emergency_contact_name: translation.fragment_profile_edittext_emergencyname,
    emergency_contact_phone: translation.fragment_profile_edittext_emergencyphone,
    food_pickup: capitalize(translation.custom_field_food_pickup),
    pick_up_name: translation.fragment_profile_edittext_pickupname,
    pick_up_phone: translation.fragment_profile_edittext_pickupphone,
    optional_details: capitalize(translation.optional_details),
    address_street_1: capitalize(translation.address_street_1),
    address_street_2: capitalize(translation.address_street_2),
    city: capitalize(translation.city),
    state: capitalize(translation.state),
    zipcode: capitalize(translation.zipcode),
    gender: capitalize(translation.gender),
    relationship_status: capitalize(translation.relationship_status),
    ethnicity: capitalize(translation.ethnicity),
    country_of_birth: capitalize(translation.country_of_birth),
    employment: capitalize(translation.employment),
    education: capitalize(translation.highest_education_obtained),
    food_preparation: capitalize(translation.custom_field_food_preparation),
    dietary_preferences: capitalize(translation.custom_field_dietary_preferences),
    dietary_preferences_option_lactose: capitalize(translation.dietary_preferences_option_lactose),
    dietary_preferences_option_gluten: capitalize(translation.dietary_preferences_option_gluten),
    dietary_preferences_option_kosher: capitalize(translation.dietary_preferences_option_kosher),
    dietary_preferences_option_halal: capitalize(translation.dietary_preferences_option_halal),
    dietary_preferences_option_dairy_free: capitalize(translation.dietary_preferences_option_dairy_free),
    dietary_preferences_option_vegetarian: capitalize(translation.dietary_preferences_option_vegetarian),
    dietary_preferences_option_vegan: capitalize(translation.dietary_preferences_option_vegan),
    dietary_preferences_option_fodmap: capitalize(translation.dietary_preferences_option_fodmap),
    dietary_preferences_option_nut_allergy: capitalize(translation.dietary_preferences_option_nut_allergy),
    dietary_preferences_option_fish_allergy: capitalize(translation.dietary_preferences_option_fish_allergy),
    housing: capitalize(translation.housing_status),
    special_circumstances: capitalize(translation.special_circumstances),
    needs_assistance: capitalize(translation.needs_assistance),
    additional_optional_details: capitalize(translation.additional_optional_details),
    adult_family_members: translation.fragment_registration_family_textview_adults,
    child_family_members: translation.fragment_registration_family_textview_children,
    senior_family_members: translation.fragment_registration_family_textview_seniors,
    baby_supplies: capitalize(translation.baby_supplies),
    textview_baby_supplies: translation.textview_baby_supplies,
  };

  useEffect(() => {
    // all fields included on the confirmation modal
    const deDupRequestedFields = Array.from(new Set(requestedFields)); // in rare situations, dups have been noted

    // all fields that are missing
    if (missingFields.length === 0) {
      setIsConfirming(true);
    }

    // by default, all fields should be checked unless input as falesy
    setCheckedList([...expandFields(deDupRequestedFields)]);
  }, []);

  useEffect(() => {
    if (user.id != profile.id) setUser(Object.assign({}, profile));
  }, [profile]);

  const setChanges = (field, value) => {
    const updatedUser = { ...user, [field]: value };
    const updatedErrors = { ...errors };
    const error = getFieldValidationErrors(field, value);

    error ? (updatedErrors[field] = getFieldValidationErrors(field, value)) : delete updatedErrors[field];

    setUser(updatedUser);
    setErrors(updatedErrors);
    setCheckedList(getCheckedList(checkedList, updatedUser, field));
  };

  const toggleIncludedField = (field) => {
    setCheckedList((prevFields) => {
      const updatedFields = prevFields.includes(field)
        ? prevFields.filter((e) => e !== field)
        : [...prevFields, field].filter(Boolean);
      return updatedFields;
    });
  };

  const confirmSaveChanges = async () => {
    const submitData = {};
    if (Object.keys(errors).length === 0 && checkedList?.length) {
      const allowedFields = expandFields(checkedList);

      allowedFields.forEach((key) => {
        const question = getQuestionByUUID(key);
        let value = user[key];

        if (question.type === 'telephone_us' && user[key]) {
          // filter out (233) 424 - 4244 formatting
          value = user[key].replace(/\D/g, '');
        }

        submitData[key] = value;
      });

      if (Object.keys(submitData).length) {
        submitData.pantry_id = pantry.pantry_id;
        submitData.allowed_fields = allowedFields;
        const allFieldsIncluded = requestedFields.length === checkedList.length;

        if (allFieldsIncluded) {
          submitData.additional_info_confirmed_at = new Date();
        }

        setIsSubmittingData(true);
        await actions.updateUser(submitData);
        await actions.showSinglePantry(pantry.nextVisit.organization_id, true); // refresh from BE
        setIsSubmittingData(false);
      }

      setIsComplete(true);
      setIsConfirming(false);
      setTyMsg(true);
    }
  };

  const renderInputElement = (question_uuid) => {
    const field = question_uuid;
    let value = user[field];

    const question = getQuestionByUUID(question_uuid);

    if (!question) {
      console.error(`renderInputElement: Undefined schema for field: ${question_uuid}`);
      Sentry.captureException(`renderInputElement: Undefined schema for field: ${question_uuid}`);
      return;
    }

    const type = question.type;

    // defaults to text input
    let keyboardType = undefined;
    // a formatter exists for this field
    if (formatters[field] || typeFormatters[type]) {
      value = convertToString(formatField(question, value, user));
    }

    if (type === 'telephone_us') {
      keyboardType = 'phone-pad';
    } else if (type === 'number') {
      keyboardType = 'number-pad';
    }

    if (type === 'number') {
      const min = question.validations.find((validation) => validation.name === 'min');
      const max = question.validations.find((validation) => validation.name === 'max');

      return (
        <View style={styles.inputContainer}>
          <PlusMinus
            value={Number(value)}
            onChange={(text) => {
              setChanges(field, text);
            }}
            {...(min && { min: min.argument })}
            {...(max && { max: max.argument })}
          />
        </View>
      );
    }
    return (
      <View style={styles.inputContainer}>
        <MyFloatingLabelInput
          label={translation[question.label]}
          onChangeText={(text) => {
            setChanges(field, text);
          }}
          value={value}
          keyboardType={keyboardType}
          disabled={false}
          width="100%"
        />
      </View>
    );
  };

  const handleForceUpdate = () => {
    forceComponentUpdate([rerenderFlag, setRerenderFlag]);
  };

  const updateDate = (field, value) => {
    setShowDateModal(false);
    setChanges(field, value);
  };

  const renderEdit = (field) => {
    // Attempt to find a compound parent or a special type parent for the field
    const parentField = schema.meta.compound_map[field] || getSpecialTypeParentField(field);

    // Determine the target index: use the parent field's index if found, otherwise use the field's own index
    const targetIndex = parentField ? requestedFields.indexOf(parentField) : requestedFields.indexOf(field);

    setDisplayIndex(targetIndex);
  };

  const renderConfirmation = () => {
    let clientUpdates = [];

    if (isSubmittingData) {
      return (
        <View style={styles.submissionPendingContainer}>
          <ActivityIndicator size="large" />
          <Text style={styles.submissionPendingText}>{translation.tefap_submission_pending}</Text>
        </View>
      );
    }

    if (isConfirming && requestedFields.length) {
      clientUpdates = Array.from(new Set(expandFields(requestedFields)));
      return (
        <View style={[styles.confirmationContainer]}>
          <Text style={styles.confirmText}>{translation.tefap_confirm_share_with_pantry}</Text>
          {clientUpdates.map((update, index) => {
            const question = getQuestionByUUID(update);

            let userValue = user[update];
            let labelText = translation[question.label];
            const type = question.type;

            switch (type) {
              case 'select_one':
                if (['country_of_birth', 'state'].includes(question.question_uuid)) {
                  break;
                }
                const option = question.options.find((option) => option.value === userValue);
                if (option) {
                  userValue = translation[option.label];
                }
                break;
              case 'address_us':
                const { label, value } = getLabelAndValueForSpecialField(update, userValue);
                labelText = translation[label];
                userValue = value;
                break;
              case 'boolean':
                if (user[update] !== null) {
                  userValue = user[update] ? translation.app_button_yes : translation.app_button_no;
                }
                break;
              case 'date':
                userValue = timeUtils.formatBirthdate(user[update]);
                break;
              case 'select_multiple':
                userValue = getUserUpdateOptionsAsString(translation, user, update);
                break;
              case 'compound':
                const childQuestion = question.questions.find(
                  (childQuestions) => childQuestions.question_uuid === update
                );
                if (!childQuestion) {
                  console.error(`renderConfirmation: childQuestion not found in schema: ${update}`);
                  Sentry.captureException(`renderConfirmation: childQuestion not found in schema: ${update}`);
                  return;
                }
                labelText = translation[childQuestion.label];
              default:
                break;
            }
            const disabled =
              alwaysInclude.includes(update) ||
              pantry.allowed_fields.includes(update) ||
              isBlankField(userValue);
            const isChecked = checkedList.includes(update);

            return (
              <View style={styles.confirmed} key={index}>
                <TouchableOpacity
                  style={styles.checkbox}
                  onPress={() => toggleIncludedField(update)}
                  testID={`confirm-checkbox-${index}`}
                  disabled={disabled}
                >
                  <Svg
                    color={disabled ? Colors.grayButton : Colors.primaryColor}
                    icon={isChecked ? 'square_checked' : 'square_unchecked'}
                  />
                </TouchableOpacity>
                <View>
                  <TouchableOpacity onPress={() => toggleIncludedField(update)} disabled={disabled}>
                    <Text style={{ fontWeight: 'bold', marginBottom: 4 }}>{labelText}</Text>
                    <Text style={{ marginBottom: 10 }}>{userValue}</Text>
                  </TouchableOpacity>
                  {errors[update] && (
                    <Text style={styles.errorText} onPress={() => renderEdit(update)}>
                      {translation[errors[update]]}
                    </Text>
                  )}
                  <Text style={styles.editText} onPress={() => renderEdit(update)}>
                    {translation.edit}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      );
    } else if (tyMsg) {
      return (
        <View style={styles.confirmationDoneContainer}>
          <Image
            source={require('../../assets/images/plentiful_fruits.png')}
            style={styles.modalImage}
            resizeMode="contain"
          />
          <Text style={styles.confirmationDoneText}>{translation.tefap_thanks_for_sharing}</Text>
        </View>
      );
    } else {
      return (
        <View style={styles.confirmationDoneContainer}>
          <Text style={styles.confirmationDoneText}>{translation.tefap_pantry_will_ask}</Text>
        </View>
      );
    }
  };

  const setDisplayIndex = (index) => {
    setRequiredIndex(index);
    setIsEditing(true);
  };

  const resetDisplayIndex = () => {
    setRequiredIndex(null);
    setIsEditing(false);
  };

  const resetForm = () => {
    setRequiredIndex(0);
    setIsConfirming(false);
    setIsComplete(false);
    setIsEditing(false);
    setTyMsg(false);
    setIsConfirmed(false);
    onBackdropPress();
  };

  const onSelectPlace = (addressObject) => {
    const updatedUser = Object.assign({}, user);
    // keep track of which fields have changed and thus should be included
    Object.keys(addressObject).forEach((field) => {
      updatedUser[field] = addressObject[field];
      setCheckedList(checkedList, updatedUser, field);
    });

    setUser(updatedUser);
  };

  const getCurrentQuestionLabel = (index) => {
    const field = isEditing ? requestedFields[index] : missingFields[index];
    const question = getQuestionByUUID(field);

    if (question) {
      return translation[question.label];
    }
  };

  const renderRequired = (index) => {
    const field = isEditing ? requestedFields[index] : missingFields[index];
    const question = getQuestionByUUID(field);

    if (!question) {
      return renderConfirmation();
    }

    const type = question.type;

    switch (type) {
      case 'date':
        return (
          <View style={styles.inputElement}>
            {showDateModal && (
              <DateSelectModal
                translation={translation}
                locale={locale}
                onCancel={() => setShowDateModal(false)}
                onSelect={(value) => updateDate(field, value)}
                date={Number.isInteger(user[field]) ? user[field] : year2000}
              />
            )}
            <TouchableOpacity onPress={() => setShowDateModal(true)} style={styles.dateField}>
              {errors[field] ? (
                <View style={styles.dateLabel}>
                  <Text style={styles.label}>{labels[question.label]}</Text>
                  <View style={styles.redLine} />
                </View>
              ) : Number.isInteger(user[field]) ? (
                <View style={styles.dateLabel}>
                  <Text style={styles.label}>{labels[question.label]}</Text>
                  <Text style={styles.detailText}>{timeUtils.formatBirthdate(user[field])}</Text>
                </View>
              ) : (
                <View style={styles.dateLabel}>
                  <Text style={styles.label}>{labels[question.label]}</Text>
                  <Text style={styles.detaiText}>{` `}</Text>
                </View>
              )}
            </TouchableOpacity>
          </View>
        );
      case 'compound':
        return (
          <View style={[styles.smallRow, styles.pickupNameRow]}>
            {Object.values(question.questions).map((question) => (
              <View key={question.question_uuid} style={styles.nameRow}>
                {renderInputElement(question.question_uuid)}
              </View>
            ))}
          </View>
        );
      case 'address_us':
        return (
          <View style={styles.infoCardRow}>
            <View style={[styles.smallRow, styles.placesContainer]}>
              <View style={[styles.inputElement, styles.placesContainer]}>
                <AddressSelector
                  translation={translation}
                  onSelect={onSelectPlace}
                  addressStreet1={user.address_street_1}
                  state={user.state}
                  city={user.city}
                  handleForceUpdate={handleForceUpdate}
                  onChange={(newValue) => setChanges('address_street_1', newValue)}
                />
              </View>
            </View>
            <View style={styles.smallRow}>
              <View style={styles.inputElement}>{renderInputElement('address_street_2')}</View>
              <View style={styles.inputElement}>{renderInputElement('city')}</View>
            </View>
            <View style={[styles.smallRow, styles.mixedInputRow]}>
              <View style={[styles.inputElement, styles.zipCodeContainer]}>
                {renderInputElement('zip_code')}
              </View>
              <View style={styles.inputElement}>
                <Selector
                  onChange={setChanges}
                  label={labels.state}
                  field={'state'}
                  currentValue={user['state']}
                  dataset={'state'}
                  translation={translation}
                />
              </View>
            </View>
          </View>
        );
      case 'select_multiple':
        return (
          <View style={styles.checkboxCardRow}>
            {question.options.map((option, index) => {
              const isChecked = user[field].includes(option.value);
              return (
                <TouchableOpacity
                  style={styles.checkboxRow}
                  key={index}
                  onPress={() =>
                    setChanges(question.question_uuid, formatField(question, option.value, user))
                  }
                  testID={`checkbox-${index}`}
                >
                  <Svg icon={isChecked ? 'square_checked' : 'square_unchecked'} style={styles.checkbox} />
                  <Text style={styles.touchableLabel}>{translation[option.label]}</Text>
                </TouchableOpacity>
              );
            })}
          </View>
        );
      case 'select_one':
        return (
          <View style={styles.smallRow}>
            <Selector
              onChange={setChanges}
              field={field}
              currentValue={user[field]}
              dataset={field}
              translation={translation}
            />
          </View>
        );
      case 'country':
        return (
          <View style={styles.smallRow}>
            <Selector
              onChange={setChanges}
              field={field}
              currentValue={user[field]}
              dataset={field}
              translation={translation}
            />
          </View>
        );
      case 'boolean':
        const checked = question.options.find((option) => option.value === 1);
        const unchecked = question.options.find((option) => option.value === 0);
        if (!checked || !unchecked) {
          console.error(`renderRequired: Undefined option for option: ${question.question_uuid}`);
          Sentry.captureException(`renderRequired: Undefined option for option: ${question.question_uuid}`);
          return;
        }
        return (
          <View>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setChanges(field, 1)}
              testID={`${field}-yes-checkbox`}
            >
              <Svg icon={user[field] === 1 ? 'square_checked' : 'square_unchecked'} style={styles.checkbox} />
              <Text style={styles.touchableLabel}>{translation[checked.label]}</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.checkboxRow}
              onPress={() => setChanges(field, 0)}
              testID={`${field}-no-checkbox`}
            >
              <Svg icon={user[field] === 0 ? 'square_checked' : 'square_unchecked'} style={styles.checkbox} />
              <Text style={styles.touchableLabel}>{translation[unchecked.label]}</Text>
            </TouchableOpacity>
          </View>
        );
      default:
        return (
          <View style={styles.inputElement}>
            <View style={styles.smallRow}>{renderInputElement(field)}</View>
          </View>
        );
    }
  };

  const handleNext = () => {
    const currentField = missingFields[requiredIndex];

    // do we need to add disabled fields
    alwaysInclude.forEach((key) => {
      if (
        missingFields.indexOf(key) == requiredIndex // if the past field was requested by the pantry
      ) {
        const updateFields = requestedFields.filter((rec) => rec !== key);

        if (!checkedList.includes(key)) {
          setCheckedList([key, ...updateFields]);
        }
      }
    });

    if (currentField) {
      setCheckedList(getCheckedList(checkedList, user, currentField));
    }

    setRequiredIndex(requiredIndex + 1);

    if (missingFields.length === requiredIndex + 1) {
      setIsConfirming(true);
    }
  };

  const handlePrev = () => {
    setRequiredIndex(requiredIndex - 1);
    setIsConfirming(false);
    setIsComplete(false);
  };

  const inputLabel = getCurrentQuestionLabel(requiredIndex);
  const inputElements = renderRequired(requiredIndex);

  return (
    <Modal
      isVisible
      deviceWidth={Layout.fullWidth}
      deviceHeight={Layout.fullHeight}
      onBackdropPress={onBackdropPress}
      style={styles.modalContainer}
    >
      <View style={styles.modalContent}>
        <View style={styles.modalHeader}>
          <Text style={styles.modalHeaderText}>{translation.tefap_missing_information}</Text>
          <Text style={styles.modalHeaderText} onPress={onBackdropPress}>
            &#215;
          </Text>
        </View>
        <ScrollView style={styles.modalBody} keyboardShouldPersistTaps="handled">
          {inputLabel && <Text style={{ marginBottom: 30, fontWeight: 'bold' }}>{inputLabel}</Text>}
          {!isConfirming && !isConfirmed && !tyMsg && requiredIndex >= 0 && (
            <>
              <Text style={{ marginBottom: 20 }}>{translation.tefap_pantry_will_ask}</Text>
            </>
          )}
          {inputElements}
        </ScrollView>
        <View style={styles.modalFooter}>
          {!isComplete && !isEditing && !tyMsg && !isSubmittingData && (
            <>
              {isConfirming && (
                <ButtonPair
                  leftText={translation.app_button_cancel}
                  rightText={translation.app_button_confirm}
                  onLeftPress={resetForm}
                  onRightPress={confirmSaveChanges}
                />
              )}

              {!isConfirming && (
                <>
                  {requiredIndex === -1 && (
                    <ButtonPair
                      leftText={translation.app_button_cancel}
                      rightText={translation.fragment_registration_family_button_next}
                      onLeftPress={onBackdropPress}
                      onRightPress={handleNext}
                    />
                  )}

                  {requiredIndex >= 0 && (
                    <ButtonPair
                      leftText={translation.fragment_registration_personal_button_back}
                      rightText={translation.fragment_registration_family_button_next}
                      onLeftPress={handlePrev}
                      onRightPress={handleNext}
                    />
                  )}
                </>
              )}
            </>
          )}
          {isEditing && !tyMsg && (
            <ButtonPair
              leftText={translation.cancel_edit}
              rightText={translation.continue}
              onLeftPress={resetDisplayIndex}
              onRightPress={resetDisplayIndex}
            />
          )}

          {tyMsg && isComplete && (
            <View style={styles.modalFooterCentered}>
              <ModalButton text={translation.app_button_close} onPress={onBackdropPress} />
            </View>
          )}
        </View>
      </View>
    </Modal>
  );
}

const ModalButton = ({ text, onPress }) => (
  <TouchableOpacity style={styles.modalButton} onPress={onPress}>
    <Text style={{ color: Colors.white }}>{text}</Text>
  </TouchableOpacity>
);

const ButtonPair = ({ leftText, rightText, onLeftPress, onRightPress }) => (
  <>
    <TouchableOpacity style={styles.modalButtonBorder} onPress={onLeftPress}>
      <Text style={{ color: Colors.darkGreen }}>{leftText}</Text>
    </TouchableOpacity>

    <ModalButton text={rightText} onPress={onRightPress} />
  </>
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    justifyContent: 'center',
    backgroundColor: Colors.white,
    width: Layout.isSmall ? 360 : 520,
  },
  modalHeader: {
    height: 66,
    width: '100%',
    padding: 16,
    flexDirection: 'row',
    backgroundColor: Colors.primaryColor,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalFooter: {
    height: 66,
    width: '100%',
    padding: 24,
    flexDirection: 'row',
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    backgroundColor: Colors.lightGrayBackground,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  modalFooterCentered: {
    height: 66,
    width: '100%',
    padding: 24,
    borderTopWidth: 1,
    borderTopColor: Colors.grayBorder,
    backgroundColor: Colors.lightGrayBackground,
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 40,
    fontFamily: 'roboto-regular',
    width: '45%',
    borderRadius: 4,
    backgroundColor: Colors.darkGreen,
  },
  modalButtonBorder: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 40,
    fontFamily: 'roboto-regular',
    width: '45%',
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.darkGreen,
  },
  modalHeaderText: {
    color: Colors.white,
    fontSize: 18,
  },
  modalBody: {
    height: 'auto',
    maxHeight: 0.6 * Layout.fullHeight,
    width: '100%',
    padding: 12,
  },
  confirmText: {
    fontSize: 16,
    marginBottom: 12,
  },
  editText: {
    color: Colors.darkGreen,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
  errorText: {
    color: Colors.brightRed,
    fontSize: Layout.isLarge ? 14 : 12,
    marginBottom: 5,
  },
  submissionPendingContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  submissionPendingText: {
    fontSize: 16,
    marginTop: 12,
    fontWeight: '500',
  },
  confirmed: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexDirection: 'row',
    marginBottom: 20,
  },
  checked: {
    marginRight: 20,
  },
  modalImage: {
    width: 300,
    height: 180,
    marginBottom: 24,
    marginTop: 24,
    marginRight: 'auto',
    marginLeft: 'auto',
  },
  infoCard: {
    padding: 16,
    borderBottomColor: Colors.grayBorder,
    borderBottomWidth: 1,
    backgroundColor: Colors.white,
  },
  infoCardRow: {
    flexDirection: 'column',
    alignItems: Layout.isCompactScreen ? 'flex-start' : 'center',
    justifyContent: 'space-between',
  },
  checkboxCardRow: {
    width: '100%',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  smallRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  checkbox: {
    width: 32,
  },
  checkboxRow: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-start',
    marginBottom: 8,
    marginTop: 8,
  },
  inputElement: {
    flex: 1,
    paddingRight: 16,
    marginBottom: 16,
  },
  placesContainer: {
    // This allows the Google Places suggestions to sit above the other form elements
    zIndex: 1,
  },
  zipCodeContainer: {
    width: Layout.isCompactScreen ? '50%' : '25%',
  },
  inputContainer: {
    flex: 1,
    marginLeft: 0,
  },
  inputField: {
    paddingLeft: 0,
  },
  floatingLabel: {
    position: 'absolute',
    top: 0,
    color: Colors.grayText,
    fontSize: 14,
    marginTop: -8,
  },
  label: {
    color: Colors.grayText,
    fontSize: 14,
  },
  touchableLabel: {
    color: Colors.grayText,
    fontSize: 14,
    marginLeft: 8,
  },
  detailText: {
    fontSize: 16,
    lineHeight: 24,
    marginTop: 8,
    color: Colors.veryDarkGray,
  },
  dateField: {
    paddingBottom: 8,
    borderBottomColor: Colors.inputGrayBorder,
    borderBottomWidth: 1,
  },
  dateLabel: {
    flexDirection: 'column',
    justifyContent: 'flex-end',
  },
  pickupNameRow: (() => {
    const layoutDirection = Layout.isCompactScreen ? 'column' : 'row';
    return {
      flexDirection: layoutDirection,
      alignItems: layoutDirection == 'column' ? 'flex-start' : 'flex-end',
      // When the flexDirection is 'column', flex-start is on the left and
      // flex-end is on the right. When the flexDirection is 'row', flex-start is
      // on the top and flex-end is on the bottom. When the flexDirection is
      // 'row', we want the inputs to be aligned on the bottom so the input lines
      // match up, regardless of how many lines the label takes up.
    };
  })(),
  nameRow: {
    marginBottom: Layout.isCompactScreen ? 16 : 0,
    marginRight: Layout.isCompactScreen ? 0 : 16,
    width: Layout.isCompactScreen ? '75%' : '50%',
  },
  confirmationContainer: {},
  confirmationDoneContainer: {
    textAlign: 'center',
    display: 'flex',
    flexDirection: 'column',
    padding: 20,
  },
  confirmationDoneText: {
    marginBottom: 10,
    fontSize: 16,
    fontWeight: '500',
  },
  mixedInputRow: {
    alignItems: 'baseline',
  },
});

MissingInfoModal.propTypes = {
  translation: PropTypes.objectOf(PropTypes.any).isRequired,
  locale: PropTypes.string.isRequired,
  actions: PropTypes.objectOf(PropTypes.any).isRequired,
  profile: PropTypes.objectOf(PropTypes.any).isRequired,
  onBackdropPress: PropTypes.func.isRequired,
  missingFields: PropTypes.array.isRequired,
  requestedFields: PropTypes.array.isRequired,
  pantry: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default MissingInfoModal;
